import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { FieldErrors } from "react-hook-form";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AddressRepository } from "@/api/address/repository";
import { AddressService } from "@/api/address/service";
import type { IAddress, IAddressSuggestion } from "@/types/address";
import type { IFormFieldProps, ISelectOption } from "@/ui-kit";
import { ComboBox, FormField } from "@/ui-kit";
import { AddressUtils } from "@/utils/address";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { InputControl } from "../InputControl";
import type { IFetchOptions } from "../useSearchOptions";
import { useSearchOptions } from "../useSearchOptions";
import { get } from "../utils";

type IProps = Partial<IFormFieldProps> & {
  name?: string;
  formElementClass?: string;
};

// Every suggestion request is a paid Google Places call: skip short queries.
const MIN_QUERY_LENGTH = 3;

const toOption = (suggestion: IAddressSuggestion): ISelectOption => {
  return {
    id: suggestion.place_id,
    text: suggestion.description,
  };
};

export const AddressControl = ({
  name = "address",
  label,
  message,
  required,
  formElementClass,
}: IProps) => {
  const { t } = useTranslation();
  const { setValue, getValues, clearErrors } = useFormContext();
  const address = useWatch({ name }) as IAddress | undefined;
  const { errors } = useFormState({ name });

  const repository = useMemo(() => {
    return new AddressRepository(new AddressService());
  }, []);

  const fetchOptions = useCallback<IFetchOptions>(
    (query, _page, signal) => {
      if (!query) {
        return Promise.resolve({ options: [], hasMore: false });
      }
      return repository
        .getAddresses({ query }, { signal })
        .then((response) => ({
          options: (response.predictions ?? []).map(toOption),
          hasMore: false,
        }));
    },
    [repository],
  );

  const { options, loading, search } = useSearchOptions(fetchOptions);

  // Text of the suggestion being picked. The form gets the name only after
  // the details load, but ComboBox searches for the picked text right away.
  const pickedTextRef = useRef<string | undefined>(undefined);

  // ComboBox puts the picked or saved address into its search box. Searching
  // for it is a paid call that narrows the list to one item, so it is skipped.
  const onSearch = useCallback(
    (query: string) => {
      const isPicked =
        query === pickedTextRef.current || query === getValues(`${name}.name`);
      search(isPicked || query.length < MIN_QUERY_LENGTH ? "" : query);
    },
    [getValues, name, search],
  );

  const [loadingDetails, setLoadingDetails] = useState(false);
  const detailsControllerRef = useRef<AbortController | null>(null);

  const onChange = useCallback(
    (option: ISelectOption | ISelectOption[] | undefined) => {
      const picked = option as ISelectOption | undefined;
      // Only the latest pick may write the address.
      detailsControllerRef.current?.abort();
      detailsControllerRef.current = null;
      pickedTextRef.current = picked?.text;

      // Cleared with the button or by erasing the text.
      if (!picked) {
        setLoadingDetails(false);
        setValue(name, AddressUtils.getEmpty(), { shouldDirty: true });
        return;
      }

      const controller = new AbortController();
      detailsControllerRef.current = controller;
      setLoadingDetails(true);

      // The form changes only once the details load, so a failed request
      // never leaves a half-updated address.
      repository
        .getAddressDetails(
          { placeId: picked.id, name: picked.text },
          { signal: controller.signal },
        )
        .then((details) => {
          setValue(name, details, { shouldDirty: true });
          clearErrors(name);
        })
        .catch(serverErrorHandler())
        .finally(() => {
          if (detailsControllerRef.current !== controller) {
            return;
          }
          detailsControllerRef.current = null;
          setLoadingDetails(false);
        });
    },
    [repository, name, setValue, clearErrors],
  );

  useEffect(() => {
    return () => {
      detailsControllerRef.current?.abort();
    };
  }, []);

  const value = useMemo((): ISelectOption | undefined => {
    if (!address?.googlePlaceId) {
      return undefined;
    }
    return { id: address.googlePlaceId, text: address.name };
  }, [address?.googlePlaceId, address?.name]);

  // Both fields are set only by picking, so either error means "pick one".
  // The other parts show their errors under their own inputs.
  const fieldErrors = get<FieldErrors<IAddress> | undefined>(errors, name);
  const errorMessage =
    fieldErrors?.googlePlaceId?.message ?? fieldErrors?.name?.message;

  // The FormField label names the ComboBox input through `<label for>`.
  const inputId = useId();

  return (
    <>
      <FormField
        htmlFor={inputId}
        label={label || t("label.address")}
        message={message ?? errorMessage}
        required={required}
        className={formElementClass}
      >
        <ComboBox
          inputId={inputId}
          options={options}
          value={value}
          onChange={onChange}
          onSearch={onSearch}
          placeholder={t("placeholder.searchAddress")}
          loading={loading || loadingDetails}
          invalid={!!errorMessage}
          openLength={MIN_QUERY_LENGTH}
        />
      </FormField>

      {address?.googlePlaceId && (
        <>
          <InputControl
            label={t("label.address_line_1")}
            name={`${name}.addressLine1`}
          />
          <InputControl
            label={t("label.address_line_2")}
            name={`${name}.addressLine2`}
          />
          <InputControl label={t("label.city")} name={`${name}.city`} />
          <InputControl label={t("label.state")} name={`${name}.state`} />
          <InputControl label={t("label.country")} name={`${name}.country`} />
          <InputControl
            label={t("label.postal_code")}
            name={`${name}.zipCode`}
          />
        </>
      )}
    </>
  );
};
